import { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import PropTypes from 'prop-types'

import Circle from '../Entities/Circle'
import Path from '../Entities/Path'
import Text from '../Entities/Text'

import CircleRenderer from './CircleRenderer'
import TextRenderer from './TextRenderer'

const defaultPathStrokeWidth = '0.1%'
const otherProcessesStrokeWidth = '0.2%'
const hoveredPathStrokeWidth = '0.3%'
const selectedPathStrokeWidth = '0.3%'

const classes = {
    rootPath: {
        strokeLinecap: 'square',
        stroke: '#FFFFFF',
        fillOpacity: 1,
        strokeOpacity: 1,
        strokeWidth: defaultPathStrokeWidth,
        '&:hover': {
            strokeWidth: hoveredPathStrokeWidth,
        },
    },
    openOuter: {
        stroke: 'red',
        fill: 'none',
    },
    closedOuter: {
        stroke: 'white',
        fill: 'hsl(0, 0%, 55%)',
    },
    inner: {
        strokeLinecap: 'square',
        strokeDasharray: 'none',
        strokeOpacity: 1,

        fill: 'none',
    },
    openInner: {
        stroke: 'purple',
        fill: 'none',
    },
    closedInner: {
        stroke: 'white',
        fill: 'hsl(0, 0%, 25%)',
    },
    notSolid: {
        strokeDasharray: '2px 2px',
    },
    etching: {
        stroke: 'green',
        strokeWidth: otherProcessesStrokeWidth,
        fill: 'none',
    },
    info: {
        stroke: 'yellow',
        strokeWidth: otherProcessesStrokeWidth,
        fill: 'none',
    },
    folding: {
        stroke: 'blue',
        strokeWidth: otherProcessesStrokeWidth,
        fill: 'none',
    },
    text: {
        fillOpacity: 1,
        fill: 'white',
        strokeWidth: 0,
        userSelect: 'none',
    },

    isSelected: {
        stroke: 'hsl(190, 100%, 50%)',
        strokeWidth: selectedPathStrokeWidth,
        '&:hover': {
            strokeWidth: selectedPathStrokeWidth,
        },
    },
}

const PathRenderer = ({ drawingPath, isPathSelected, showOpenPathHighlight, usingCartesianCoordinates = true }) => {
    const [path, setPath] = useState(null)

    useEffect(() => {
        if (drawingPath) {
            // Need to create a Path object to use render methods
            setPath(new Path(drawingPath))
        }
    }, [drawingPath])

    const getPathComponent = () => {
        if (!path || !path.entities || path.entities.length === 0) {
            return null
        }

        const isOuter = path.isOuter()
        const isClosed = path.isClosed
        const isSolid = path.isSolid
        const isCutting = path.layer.isCutting()
        const isEtching = path.layer.isEtching()
        const isInfo = path.layer.isInfo()
        const isText = path.isText()
        const isFolding = path.layer.isFolding()

        const pathStyle = Object.assign(
            {},
            isOuter && !isClosed ? classes.openOuter : {},
            isOuter && isClosed ? classes.closedOuter : {},
            !isOuter ? classes.inner : {},
            !isOuter && !isClosed ? classes.openInner : {},
            !isOuter && isClosed && isCutting ? classes.closedInner : {},
            !isSolid ? classes.notSolid : {},
            isCutting ? classes.cutting : {},
            isEtching ? classes.etching : {},
            isInfo ? classes.info : {},
            isText ? classes.text : {},
            isFolding ? classes.folding : {}
        )

        if (path.entities.length === 1 && path.entities[0].type === Circle.ToolpathType) {
            return (
                <CircleRenderer
                    circle={path.entities[0]}
                    path={path}
                    sx={[classes.rootPath, pathStyle, isPathSelected && classes.isSelected]}
                />
            )
        } else if (path.entities.length === 1 && path.entities[0].type === Text.ToolpathType) {
            return (
                <TextRenderer
                    hidden={true}
                    path={path}
                    sx={[classes.rootPath, pathStyle, isPathSelected && classes.isSelected]}
                    textEntity={path.entities[0]}
                    usingCartesianCoordinates={usingCartesianCoordinates}
                />
            )
        }

        let pathStr = ''
        let lastPathPt = null

        for (let i = 0; i < path.entities.length; i++) {
            const e = path.entities[i]
            if (e.type === Circle.ToolpathType || e.type === Text.ToolpathType) {
                throw new Error('Unexpected entity in path - ' + e.type)
            } else {
                pathStr += e.getSvgPathStr(lastPathPt)
                lastPathPt = e.endPoint
            }
        }

        return (
            <Box
                component="path"
                d={pathStr}
                id={path.pathId}
                markerEnd={!path.isClosed && showOpenPathHighlight ? 'url(#open-path-highlight)' : null}
                markerStart={!path.isClosed && showOpenPathHighlight ? 'url(#open-path-highlight)' : null}
                sx={[classes.rootPath, pathStyle, isPathSelected && classes.isSelected]}
            />
        )
    }

    return getPathComponent()
}

PathRenderer.propTypes = {
    drawingPath: PropTypes.object,
    isPathSelected: PropTypes.bool,
    showOpenPathHighlight: PropTypes.bool,
    usingCartesianCoordinates: PropTypes.bool,
}

export default PathRenderer
